// Live SVG renderer for a Word Art preset: a heavy display face plus the
// preset's warp (slant), 3D extrude, or hard shadow. Mirrors LayoutText's
// measure-and-scale so the word fills the placement width without distorting
// glyphs. The face colour is the customer's picked thread; the extrude /
// shadow colour derives from it (see resolveWordArtColours). Word Art is a
// single line - stray newlines collapse to one row so the classic one-word
// look holds.

import * as React from "react";
import type { WordArtPreset } from "../wordart/presets";
import { resolveWordArtColours } from "../wordart/presets";

type WordArtBox = { cx: number; cy: number; w: number; fontMm: number };

export function WordArtText({
  box,
  text,
  preset,
  colour,
  filterId = "rk-pc-pv-stitch",
}: {
  box: WordArtBox;
  text: string;
  preset: WordArtPreset;
  /** The face colour (customer's picked thread hex). */
  colour: string;
  /** Stitch filter to apply, or null to skip. Gallery tiles pass null: a
   *  dangling filter ref (no PreviewDefs in scope) hides the element. */
  filterId?: string | null;
}) {
  const ref = React.useRef<SVGTextElement>(null);
  const [scale, setScale] = React.useState(1);

  const raw = (preset.forceUppercase ? text.toUpperCase() : text)
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !raw || box.w <= 0) return;
    const measure = () => {
      let nat = 0;
      try {
        nat = el.getComputedTextLength();
      } catch {
        nat = 0;
      }
      if (nat > 0) setScale(Math.max(0.25, Math.min(2.4, box.w / nat)));
    };
    measure();
    // Re-measure once the heavy display webfont loads - the natural width
    // changes when it swaps in for the fallback face.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
  }, [raw, box.w, box.fontMm, preset.id]);

  if (!raw) return null;

  const { face, second } = resolveWordArtColours(preset, colour);
  const fm = box.fontMm;
  const yBias = fm * 0.34;
  const filter = filterId ? `url(#${filterId})` : undefined;
  const family = preset.fontFamily;

  // The layer behind the face: an extruded 3D side (stepped copies) or a
  // single hard shadow. Both stitch as one solid second thread colour.
  const behind: React.ReactNode[] = [];
  if (preset.extrude) {
    const { dx, dy, depth } = preset.extrude;
    for (let i = depth; i >= 1; i--) {
      behind.push(
        <text
          key={`e${i}`}
          x={dx * fm * i}
          y={yBias + dy * fm * i}
          textAnchor="middle"
          dominantBaseline="alphabetic"
          fontFamily={family}
          fontSize={fm}
          fill={second}
        >
          {raw}
        </text>,
      );
    }
  } else if (preset.shadow) {
    behind.push(
      <text
        key="sh"
        x={preset.shadow.dx * fm}
        y={yBias + preset.shadow.dy * fm}
        textAnchor="middle"
        dominantBaseline="alphabetic"
        fontFamily={family}
        fontSize={fm}
        fill={second}
      >
        {raw}
      </text>,
    );
  }

  const rotate =
    preset.warp === "slant-up" && preset.slantDeg
      ? ` rotate(${preset.slantDeg})`
      : "";

  return (
    <g transform={`translate(${box.cx} ${box.cy}) scale(${scale})${rotate}`}>
      {behind.length > 0 ? <g filter={filter}>{behind}</g> : null}
      <text
        ref={ref}
        x={0}
        y={yBias}
        textAnchor="middle"
        dominantBaseline="alphabetic"
        fontFamily={family}
        fontSize={fm}
        fill={face}
        filter={filter}
      >
        {raw}
      </text>
    </g>
  );
}
